// src/services/notificationService.js
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

class NotificationService {
  constructor() {
    // Check if Notification API exists before trying to access it.
    if ('Notification' in window) {
      this.permission = Notification.permission;
    } else {
      // If not supported, set a default 'unsupported' status.
      this.permission = 'unsupported';
    }
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Notifications not supported');
    }

    const permission = await Notification.requestPermission();
    this.permission = permission;
    return permission === 'granted';
  }

  /**
   * Get current permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }

  /**
   * Save notification preferences to Firebase
   */
  async savePreferences(userId, preferences) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'notifications': preferences,
        'notifications.permission': this.permission,
        'notifications.updatedAt': new Date()
      });
       if ('caches' in window) {
      const cache = await caches.open('user-preferences');
      const response = new Response(JSON.stringify(preferences));
      await cache.put('/notification-prefs', response);
    }
      return true;
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      return false;
    }
  }

  /**
   * Load notification preferences from Firebase
   */
  async loadPreferences(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        return userDoc.data().notifications || this.getDefaultPreferences();
      }

      return this.getDefaultPreferences();
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      return this.getDefaultPreferences();
    }
  }

  /**
   * Get default notification preferences
   */
  getDefaultPreferences() {
    return {
      enabled: false,
      permission: this.permission,
      studyReminders: true,
      streakReminders: true,
      reviewReminders: true,
      achievementAlerts: true,
      dailyReminderTime: '18:00', // 6 PM
      quietHoursStart: '22:00', // 10 PM
      quietHoursEnd: '08:00' // 8 AM
    };
  }

  /**
   * Show a local notification
   */
  async showNotification(title, options = {}) {
    if (this.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;

      const defaultOptions = {
        icon: '/icon-192x192.png',
        badge: '/icon-96x96.png',
        vibrate: [200, 100, 200],
        requireInteraction: false,
        ...options
      };

      return await registration.showNotification(title, defaultOptions);
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Schedule a notification (using local storage + service worker)
   */
  async scheduleNotification(id, title, options, scheduledTime) {
    const notifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');

    notifications.push({
      id,
      title,
      options,
      scheduledTime: scheduledTime.getTime(),
      created: Date.now()
    });

    localStorage.setItem('scheduledNotifications', JSON.stringify(notifications));
  }

  /**
   * Cancel a scheduled notification
   */
  cancelScheduledNotification(id) {
    const notifications = JSON.parse(localStorage.getItem('scheduledNotifications') || '[]');
    const filtered = notifications.filter(n => n.id !== id);
    localStorage.setItem('scheduledNotifications', JSON.stringify(filtered));
  }

  /**
   * Send streak reminder notification
   */
  async sendStreakReminder(currentStreak) {
    return await this.showNotification('Keep Your Streak Alive! 🔥', {
      body: `You're on a ${currentStreak} day streak! Complete today's lesson to keep it going.`,
      tag: 'streak-reminder',
      data: { type: 'streak', url: '/dashboard' }
    });
  }

  /**
   * Send review reminder notification
   */
  async sendReviewReminder(dueCards) {
    return await this.showNotification('Time to Review! 📚', {
      body: `You have ${dueCards} cards due for review. Let's reinforce your learning!`,
      tag: 'review-reminder',
      data: { type: 'review', url: '/practice/vocabulary' }
    });
  }

  /**
   * Send achievement notification
   */
  async sendAchievementNotification(achievementName) {
    return await this.showNotification('Achievement Unlocked! 🏆', {
      body: `Congratulations! You've earned: ${achievementName}`,
      tag: 'achievement',
      requireInteraction: true,
      data: { type: 'achievement', url: '/profile' }
    });
  }

  /**
   * Send daily study reminder
   */
  async sendDailyReminder() {
    return await this.showNotification('Time to Study! 📖', {
      body: 'Keep up your daily practice. Your German skills are waiting!',
      tag: 'daily-reminder',
      data: { type: 'study', url: '/dashboard' }
    });
  }
}

const notificationService = new NotificationService();
export default notificationService;